/**
 * OIMS v2 - Dashboard module
 * Pulse Bento: Infrastructure list + Bento grid + tactical status bar
 */

import { escapeHtml } from './utils.js';

let state = null;
let refreshTimer = null;
let dashInstances = null;  // store for badge dropdown
const REFRESH_INTERVAL = 30000; // 30 seconds

// --- Init ---

export function initDashboard(appState) {
  state = appState;

  // Listen for tab changes - refresh when dashboard becomes visible
  document.addEventListener('oims:tab-change', (e) => {
    if (e.detail.tab === 'dashboard') {
      loadDashboard();
      startAutoRefresh();
    } else {
      stopAutoRefresh();
    }
  });

  // Dashboard status badges -> show affected instances dropdown
  const statusBar = document.getElementById('dashStatusBar');
  let badgeDropdown = null;

  statusBar && statusBar.addEventListener('click', (e) => {
    const badge = e.target.closest('.dash-metric-badge');
    if (!badge || !dashInstances) return;

    // Determine which status was clicked
    let filterStatus;
    if (badge.id === 'dashHealthyCount') filterStatus = 'healthy';
    else if (badge.id === 'dashDegradedCount') filterStatus = 'degraded';
    else if (badge.id === 'dashUnhealthyCount') filterStatus = 'unhealthy';
    else return;

    const matches = dashInstances.filter(i => i.status === filterStatus);
    if (matches.length === 0) {
      if (badgeDropdown) badgeDropdown.classList.add('d-none');
      return;
    }

    // Create dropdown if not exists
    if (!badgeDropdown) {
      badgeDropdown = document.createElement('div');
      badgeDropdown.className = 'pill-dropdown d-none';
      badgeDropdown.id = 'dashBadgeDropdown';
      statusBar.appendChild(badgeDropdown);
    }

    // Position below the badge
    const rect = badge.getBoundingClientRect();
    const barRect = statusBar.getBoundingClientRect();
    badgeDropdown.style.top = `${rect.bottom - barRect.top + 4}px`;
    badgeDropdown.style.left = `${rect.left - barRect.left}px`;

    const statusIcon = filterStatus === 'healthy' ? 'bi-check-circle-fill text-success' :
                       filterStatus === 'degraded' ? 'bi-exclamation-triangle-fill text-warning' :
                       'bi-x-circle-fill text-danger';
    const label = filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1);

    let html = `<div class="pill-dropdown-header">${matches.length} ${escapeHtml(label)} Instance${matches.length !== 1 ? 's' : ''}</div>`;
    matches.forEach(inst => {
      const detail = inst.warning || inst.error || inst.group_name || '';
      html += `<a class="pill-dropdown-item" href="#" data-instance-id="${escapeHtml(inst.instance_id)}" data-group-id="${escapeHtml(inst.group)}">
        <i class="bi ${statusIcon}"></i>
        <div class="pill-dropdown-item-text">
          <span class="pill-dropdown-name">${escapeHtml(inst.name || inst.instance_id)}</span>
          ${detail ? `<span class="pill-dropdown-detail">${escapeHtml(detail)}</span>` : ''}
        </div>
        <i class="bi bi-arrow-right"></i>
      </a>`;
    });

    badgeDropdown.innerHTML = html;
    badgeDropdown.classList.remove('d-none');
  });

  // Badge dropdown item click -> navigate to status tab + scroll to instance
  statusBar && statusBar.addEventListener('click', (e) => {
    const item = e.target.closest('.pill-dropdown-item');
    if (!item) return;
    e.preventDefault();

    const instanceId = item.dataset.instanceId;
    const groupId = item.dataset.groupId;
    if (badgeDropdown) badgeDropdown.classList.add('d-none');

    // Navigate to status tab and highlight instance
    document.dispatchEvent(new CustomEvent('oims:navigate-status', {
      detail: { instanceId, groupId }
    }));
    const link = document.querySelector('[data-tab-target="status"]');
    if (link) link.click();
  });

  // Click outside to dismiss
  document.addEventListener('click', (e) => {
    if (badgeDropdown && !badgeDropdown.contains(e.target) && !e.target.closest('.dash-metric-badge')) {
      badgeDropdown.classList.add('d-none');
    }
  });
}

export async function loadDashboard() {
  if (!state?.accessToken) return;

  try {
    const { data } = await axios.get('/dashboard/summary');
    dashInstances = data.instances || [];
    renderStatusBar(data.system);
    renderHealthScore(data.system);
    renderInfraList(data.groups, data.instances);
    renderQuickActions(data.user);
    renderSystemMetrics(data.instances);
    renderActivityStream(data.recent_events);
    renderGroupsSummary(data.groups);
    updateLastRefresh();
  } catch (e) {
    console.error('Dashboard load error:', e);
    renderStatusBarError();
  }
}

function startAutoRefresh() {
  stopAutoRefresh();
  refreshTimer = setInterval(loadDashboard, REFRESH_INTERVAL);
}

function stopAutoRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer);
    refreshTimer = null;
  }
}

// --- Status Bar ---

function renderStatusBar(system) {
  const bar = document.getElementById('dashStatusBar');
  const text = document.getElementById('dashSystemText');
  if (!bar || !text) return;

  bar.classList.remove('dash-bar-nominal', 'dash-bar-degraded', 'dash-bar-critical', 'dash-bar-unknown');

  const statusMap = {
    nominal: 'dash-bar-nominal',
    degraded: 'dash-bar-degraded',
    critical: 'dash-bar-critical',
    unknown: 'dash-bar-unknown',
  };
  bar.classList.add(statusMap[system.status] || 'dash-bar-unknown');
  text.textContent = system.message;

  animateCounter('dashHealthyCount', system.healthy);
  animateCounter('dashDegradedCount', system.degraded);
  animateCounter('dashUnhealthyCount', system.unhealthy);
}

function renderStatusBarError() {
  const bar = document.getElementById('dashStatusBar');
  const text = document.getElementById('dashSystemText');
  if (bar) bar.classList.add('dash-bar-unknown');
  if (text) text.textContent = 'CONNECTION ERROR';
}

// --- Health Score ---

function renderHealthScore(system) {
  const total = system.total_instances || 1;
  const score = total > 0 ? Math.round((system.healthy / total) * 100) : 0;

  const ring = document.getElementById('dashRingProgress');
  if (ring) {
    const circumference = 2 * Math.PI * 52;
    const offset = circumference - (score / 100) * circumference;
    ring.style.strokeDasharray = circumference;
    ring.style.strokeDashoffset = offset;

    ring.classList.remove('dash-ring-good', 'dash-ring-warn', 'dash-ring-bad');
    if (score >= 90) ring.classList.add('dash-ring-good');
    else if (score >= 70) ring.classList.add('dash-ring-warn');
    else ring.classList.add('dash-ring-bad');
  }

  const valueEl = document.getElementById('dashScoreValue');
  if (valueEl) animateNumber(valueEl, score, '%');

  const detailEl = document.getElementById('dashTotalInstances');
  if (detailEl) detailEl.textContent = total;

  const card = document.getElementById('dashScoreCard');
  if (card) {
    card.classList.remove('dash-score-good', 'dash-score-warn', 'dash-score-bad');
    if (score >= 90) card.classList.add('dash-score-good');
    else if (score >= 70) card.classList.add('dash-score-warn');
    else card.classList.add('dash-score-bad');
  }
}

// --- Infrastructure List (redesigned from heatmap) ---

function renderInfraList(groups, instances) {
  const container = document.getElementById('dashInfraList');
  if (!container) return;

  if (!instances || instances.length === 0) {
    container.innerHTML = `
      <div class="dash-infra-empty">
        <i class="bi bi-shield-exclamation"></i>
        <span>No instances available</span>
      </div>`;
    return;
  }

  // Group instances by group
  const grouped = {};
  for (const inst of instances) {
    const group = inst.group;
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push(inst);
  }

  let html = '';
  for (const g of groups) {
    const groupInstances = grouped[g.id] || [];
    if (groupInstances.length === 0) continue;

    html += `<div class="dash-infra-group">`;
    html += `<div class="dash-infra-group-header">${escapeHtml(g.label)}</div>`;

    for (const inst of groupInstances) {
      const status = inst.status || 'unknown';
      const m = inst.metrics || {};
      // Pick best memory value: system % > JVM % > MB fallback
      const mem = m.memoryPercent || m.memoryUsagePercent || m.memory || 0;
      const memDisplay = mem ? `${Math.round(mem)}%` : (m.memoryMB ? `${m.memoryMB}MB` : '--');
      const name = escapeHtml(inst.name || inst.instance_id);
      const pulse = (status === 'degraded' || status === 'unhealthy') ? ' dash-infra-pulse' : '';

      // Build compact detail chips
      let chips = '';
      if (m.cpu !== undefined) chips += `<span class="dash-infra-chip">CPU:${m.cpu}%</span>`;
      if (m.diskPercent !== undefined) chips += `<span class="dash-infra-chip${m.diskPercent >= 80 ? ' dash-chip-warn' : ''}">Disk:${m.diskPercent}%</span>`;

      html += `<div class="dash-infra-row${pulse}" data-instance="${escapeHtml(inst.instance_id)}" data-group="${escapeHtml(inst.group)}">
        <span class="dash-infra-dot dash-dot-${status}"></span>
        <span class="dash-infra-name">${name}</span>
        <span class="dash-infra-mem-wrap">
          <span class="dash-infra-mem-bar">
            <span class="dash-infra-mem-fill dash-fill-${status}" style="width:${Math.min(mem, 100)}%"></span>
          </span>
          <span class="dash-infra-mem-val">${memDisplay}</span>
        </span>
        ${chips ? `<span class="dash-infra-chips">${chips}</span>` : ''}
      </div>`;
    }

    html += `</div>`;
  }

  container.innerHTML = html;

  // Add click handlers - navigate to status tab and highlight the instance
  container.querySelectorAll('.dash-infra-row').forEach(row => {
    row.style.cursor = 'pointer';
    row.addEventListener('click', () => {
      const instanceId = row.dataset.instance;
      const groupId = row.dataset.group;
      // Dispatch event so status tab can scroll to + highlight the instance
      document.dispatchEvent(new CustomEvent('oims:navigate-status', {
        detail: { instanceId, groupId }
      }));
      const link = document.querySelector('[data-tab-target="status"]');
      if (link) link.click();
    });
  });
}

// --- System Metrics (NEW) ---

function renderSystemMetrics(instances) {
  if (!instances || instances.length === 0) return;

  // Calculate avg memory (prefer system % > JVM % > container %)
  const memValues = instances
    .map(i => i.metrics?.memoryPercent || i.metrics?.memoryUsagePercent || i.metrics?.memory || 0)
    .filter(v => v > 0);
  const avgMem = memValues.length > 0 ? memValues.reduce((a, b) => a + b, 0) / memValues.length : 0;

  // Calculate avg response time
  const rtValues = instances
    .map(i => i.response_time_ms || 0)
    .filter(v => v > 0);
  const avgRt = rtValues.length > 0 ? rtValues.reduce((a, b) => a + b, 0) / rtValues.length : 0;

  // Peak memory
  const peakMem = memValues.length > 0 ? Math.max(...memValues) : 0;

  // Slowest response time
  const slowestRt = rtValues.length > 0 ? Math.max(...rtValues) : 0;

  // Render values
  const avgMemEl = document.getElementById('dashAvgMemory');
  if (avgMemEl) avgMemEl.textContent = `${Math.round(avgMem)}%`;

  const avgRtEl = document.getElementById('dashAvgResponse');
  if (avgRtEl) avgRtEl.textContent = `${Math.round(avgRt)}ms`;

  const peakMemEl = document.getElementById('dashPeakMemory');
  if (peakMemEl) peakMemEl.textContent = `${Math.round(peakMem)}%`;

  const slowestRtEl = document.getElementById('dashSlowestResponse');
  if (slowestRtEl) slowestRtEl.textContent = `${Math.round(slowestRt)}ms`;

  // Render bars (memory: 0-100%, response: 0-5000ms scale)
  const avgMemBar = document.getElementById('dashAvgMemoryBar');
  if (avgMemBar) avgMemBar.style.width = `${Math.min(avgMem, 100)}%`;

  const avgRtBar = document.getElementById('dashAvgResponseBar');
  if (avgRtBar) avgRtBar.style.width = `${Math.min((avgRt / 5000) * 100, 100)}%`;

  const peakMemBar = document.getElementById('dashPeakMemoryBar');
  if (peakMemBar) peakMemBar.style.width = `${Math.min(peakMem, 100)}%`;

  const slowestRtBar = document.getElementById('dashSlowestResponseBar');
  if (slowestRtBar) slowestRtBar.style.width = `${Math.min((slowestRt / 5000) * 100, 100)}%`;
}

// --- Quick Actions ---

const ACTION_ICONS = {
  restart_birt: 'bi-arrow-repeat',
  restart_wildfly: 'bi-arrow-repeat',
  restart_jbpm: 'bi-arrow-repeat',
  restart_docker: 'bi-box',
  restart_dep: 'bi-boxes',
  restart_fe: 'bi-window',
  restart_be: 'bi-server',
  restart_iis_apppool: 'bi-windows',
  backup_sql: 'bi-database',
  restart_sql: 'bi-database-gear',
};

const ACTION_LABELS = {
  restart_birt: 'BIRT',
  restart_wildfly: 'WildFly',
  restart_jbpm: 'jBPM',
  restart_docker: 'Docker',
  restart_dep: 'DEP',
  restart_fe: 'Frontend',
  restart_be: 'Backend',
  restart_iis_apppool: 'IIS',
  backup_sql: 'SQL Backup',
  restart_sql: 'SQL',
};

function renderQuickActions(user) {
  const container = document.getElementById('dashQuickActions');
  if (!container) return;

  // Only show restart/backup actions as quick action buttons (not allow_ip, schedule_instance, etc.)
  const QUICK_ACTION_IDS = new Set(Object.keys(ACTION_ICONS));
  const actions = (user.allowed_actions || []).filter(a => QUICK_ACTION_IDS.has(a)).slice(0, 6);

  if (actions.length === 0) {
    container.innerHTML = `<div class="dash-actions-empty">No actions available</div>`;
    return;
  }

  let html = '';
  for (const action of actions) {
    const icon = ACTION_ICONS[action] || 'bi-play-circle';
    const label = ACTION_LABELS[action] || action;
    html += `<button class="dash-action-btn" data-action="${escapeHtml(action)}" title="${escapeHtml(label)}">
      <i class="bi ${icon}"></i>
      <span>${escapeHtml(label)}</span>
    </button>`;
  }

  // IIS and JBPM quick-nav buttons (only if user has permission)
  const allActions = user.allowed_actions || [];
  if (allActions.includes('restart_iis_apppool')) {
    html += `<button class="dash-action-btn dash-action-nav" data-nav="windows" title="IIS">
      <i class="bi bi-windows"></i>
      <span>IIS</span>
    </button>`;
  }
  if (allActions.includes('restart_jbpm') && !actions.includes('restart_jbpm')) {
    html += `<button class="dash-action-btn" data-action="restart_jbpm" title="JBPM">
      <i class="bi bi-recycle"></i>
      <span>JBPM</span>
    </button>`;
  }

  container.innerHTML = html;

  // Action buttons -> navigate to Actions tab and pre-select the action
  container.querySelectorAll('.dash-action-btn[data-action]').forEach(btn => {
    btn.addEventListener('click', () => {
      const actionId = btn.dataset.action;
      // Dispatch event so actions tab can pre-select the action
      document.dispatchEvent(new CustomEvent('oims:navigate-action', {
        detail: { actionId }
      }));
      const link = document.querySelector('[data-tab-target="actions"]');
      if (link) link.click();
    });
  });

  // Nav buttons -> navigate to respective tab
  container.querySelectorAll('.dash-action-btn[data-nav]').forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.nav;
      const link = document.querySelector(`[data-tab-target="${tab}"]`);
      if (link) link.click();
    });
  });
}

// --- Activity Stream ---

function renderActivityStream(events) {
  const container = document.getElementById('dashActivityList');
  if (!container) return;

  if (!events || events.length === 0) {
    container.innerHTML = `
      <div class="dash-activity-empty">
        <i class="bi bi-clock-history"></i>
        <span>No recent activity</span>
      </div>`;
    return;
  }

  let html = '';
  for (const event of events.slice().reverse()) {
    const type = event.type || 'unknown';
    const ts = event.timestamp ? timeAgo(event.timestamp) : '';

    let icon, iconClass, title, detail;

    if (type === 'health_alert') {
      const newStatus = event.new_status || 'unknown';
      icon = newStatus === 'unhealthy' ? 'bi-exclamation-triangle-fill' :
             newStatus === 'degraded' ? 'bi-exclamation-circle-fill' :
             'bi-check-circle-fill';
      iconClass = newStatus === 'unhealthy' ? 'dash-act-danger' :
                  newStatus === 'degraded' ? 'dash-act-warning' :
                  'dash-act-success';
      title = `${escapeHtml(event.instance_name || event.instance_id || '')} ${newStatus}`;
      detail = escapeHtml(event.group_name || '');
    } else if (type === 'user_action') {
      const status = event.status || 'sent';
      icon = status === 'completed' ? 'bi-check-circle-fill' :
             status === 'failed' ? 'bi-x-circle-fill' :
             'bi-play-circle-fill';
      iconClass = status === 'completed' ? 'dash-act-success' :
                  status === 'failed' ? 'dash-act-danger' :
                  'dash-act-info';
      title = escapeHtml(event.action_label || event.action_id || 'Action');
      detail = `${escapeHtml(event.instance_name || '')} by ${escapeHtml(event.user || '')}`;
    } else if (type === 'approval_update') {
      const status = event.status || 'pending';
      icon = status === 'executing' ? 'bi-arrow-repeat' :
             status === 'pending' ? 'bi-shield-lock-fill' :
             status === 'denied' ? 'bi-shield-x' :
             'bi-shield-check';
      iconClass = status === 'executing' ? 'dash-act-info' :
                  status === 'pending' ? 'dash-act-warning' :
                  status === 'denied' ? 'dash-act-danger' :
                  'dash-act-success';
      const label = escapeHtml(event.action_label || event.action_id || 'Action');
      const who = escapeHtml(event.approver || event.requester || '');
      title = `${label} — ${status}`;
      detail = `${escapeHtml(event.instance_name || '')}${who ? ` by ${who}` : ''}`;
    } else {
      icon = 'bi-info-circle';
      iconClass = 'dash-act-info';
      title = 'Event';
      detail = '';
    }

    html += `<div class="dash-activity-item">
      <div class="dash-activity-icon ${iconClass}"><i class="bi ${icon}"></i></div>
      <div class="dash-activity-content">
        <div class="dash-activity-title">${title}</div>
        <div class="dash-activity-detail">${detail}</div>
      </div>
      <div class="dash-activity-time">${ts}</div>
    </div>`;
  }

  container.innerHTML = html;
}

// --- Group Summary Cards ---

function renderGroupsSummary(groups) {
  const container = document.getElementById('dashGroupsRow');
  if (!container) return;

  if (!groups || groups.length === 0) {
    container.innerHTML = '';
    return;
  }

  let html = '';
  for (const g of groups) {
    const total = g.instance_count || 0;
    const healthy = g.healthy || 0;
    const pct = total > 0 ? Math.round((healthy / total) * 100) : 0;

    let statusClass = 'dash-group-good';
    if (g.unhealthy > 0) statusClass = 'dash-group-bad';
    else if (g.degraded > 0) statusClass = 'dash-group-warn';

    html += `<div class="dash-group-card ${statusClass}">
      <div class="dash-group-name">${escapeHtml(g.label)}</div>
      <div class="dash-group-count">${healthy}/${total}</div>
      <div class="dash-group-bar">
        <div class="dash-group-fill" style="width:${pct}%"></div>
      </div>
    </div>`;
  }

  container.innerHTML = html;
}

// --- Helpers ---

function animateNumber(el, target, suffix = '') {
  const current = parseInt(el.textContent) || 0;
  if (current === target) return;

  const duration = 600;
  const start = performance.now();

  function tick(now) {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const eased = 1 - Math.pow(1 - progress, 3);
    const value = Math.round(current + (target - current) * eased);
    el.textContent = value + suffix;
    if (progress < 1) requestAnimationFrame(tick);
  }

  requestAnimationFrame(tick);
}

function animateCounter(parentId, value) {
  const parent = document.getElementById(parentId);
  if (!parent) return;
  const span = parent.querySelector('span');
  if (span) span.textContent = value;
}

function timeAgo(timestamp) {
  const now = Date.now();
  let ts;
  if (typeof timestamp === 'number') {
    ts = timestamp < 1e12 ? timestamp * 1000 : timestamp;
  } else {
    ts = new Date(timestamp).getTime();
  }
  const diff = Math.floor((now - ts) / 1000);

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function updateLastRefresh() {
  const el = document.getElementById('dashLastUpdate');
  if (el) {
    const now = new Date();
    el.textContent = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
}

// Listen for SSE notifications to update activity stream in real-time
document.addEventListener('oims:notification', (e) => {
  const dashTab = document.getElementById('dashboard');
  if (!dashTab?.classList.contains('active')) return;
  loadDashboard();
});
